import type { editor } from 'monaco-editor';
import type { CardComponentList, ICardComponent } from '../Components/CodeEditor/Devices/DeviceCardBuilder';
import { CardComponents } from '../Components/CodeEditor/Devices/DeviceCardBuilder';
import { initializeParameter } from '../Components/CodeEditor/Devices/DeviceCardBuilder';
import type { DeviceCardProps } from '../Components/CodeEditor/Devices/DeviceCardProps';
import type { FrameworkClass } from '../framework/framework.d';
import { FrameworkClasses } from '../framework/frameworkClasses';
import type { DriveUnitPortOccupancy } from '../redux/ApplicationSlice';
import { DRIVE_UNIT_PORT_MAX, DRIVE_UNIT_PORT_MIN, isUnsupportedDriveUnitPortRawValue } from './driveUnitPortSelection';
import { readConstructorKeywordValue, resolveConstructorDeclarationLineIndex, splitCodeLines } from './pythonSourceScanUtils';
import { followerMotorPortElectronic, parseMechanismFollowerMotorsFromProjectCode } from './mechanismFollowerMotorUtils';

export type DriveUnitPortInitEntry = {
  parameter: string;
  constructorArg: string;
};

const VALID_HARDWARE_CARD_TYPES = new Set<string>(Object.keys(CardComponents));

/** Drive-unit port bindings use constructor keywords that include `port` (e.g. `port`, `motor_port`). */
export function isPortConstructorArg(constructorArg: string): boolean {
  return constructorArg.includes('port');
}

/**
 * Initialization rows that bind a drive-unit port (1–8) to a constructor keyword in snippets.
 * Derived from framework metadata (`constructor_arg` contains `port`), not a hardcoded device list.
 */
export function getDriveUnitPortInitEntries(frameworkClass: FrameworkClass): DriveUnitPortInitEntry[] {
  const entries: DriveUnitPortInitEntry[] = [];
  for (const item of frameworkClass.initialization ?? []) {
    const constructorArg = item.constructor_arg;
    if (typeof constructorArg !== 'string' || !isPortConstructorArg(constructorArg)) continue;
    const parameter = item.parameter;
    if (typeof parameter !== 'string' || parameter.length === 0) continue;
    entries.push({ parameter, constructorArg });
  }
  return entries;
}

const FRAMEWORK_CLASSES_WITH_CONSTRUCTOR_PORT = FrameworkClasses.filter((frameworkClass) => getDriveUnitPortInitEntries(frameworkClass).length > 0);

/** Framework class names that may appear in electronics/mechanism drag snippets with a port literal. */
export const DRIVE_UNIT_PORT_SNIPPET_CLASS_NAMES: readonly string[] = FRAMEWORK_CLASSES_WITH_CONSTRUCTOR_PORT.map(
  (frameworkClass) => frameworkClass.name
);

const DRIVE_UNIT_PORT_SNIPPET_CLASS_SET = new Set<string>(DRIVE_UNIT_PORT_SNIPPET_CLASS_NAMES);

export function isDriveUnitPortSnippetClassName(className: string): boolean {
  return DRIVE_UNIT_PORT_SNIPPET_CLASS_SET.has(className);
}

export function findFrameworkClassWithDriveUnitPort(className: string): FrameworkClass | undefined {
  if (!isDriveUnitPortSnippetClassName(className)) return undefined;
  return FrameworkClasses.find((frameworkClass) => frameworkClass.name === className);
}

function isHardwareCardType(uiPanel: string | null): uiPanel is CardComponentList {
  return uiPanel !== null && VALID_HARDWARE_CARD_TYPES.has(uiPanel);
}

function parseDriveUnitPortValue(raw: unknown): number | null {
  if (isUnsupportedDriveUnitPortRawValue(raw)) return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  const n = Number.parseInt(String(raw ?? ''), 10);
  return Number.isFinite(n) ? n : null;
}

function resolvePortFromProjectCode(
  projectCode: string,
  lineNumber: number,
  parentName: string,
  frameworkClass: FrameworkClass,
  parameter: string
): number | null {
  const portEntry = getDriveUnitPortInitEntries(frameworkClass).find((entry) => entry.parameter === parameter);
  if (!portEntry) return null;

  const lines = splitCodeLines(projectCode);
  const lineIndex = parentName
    ? resolveConstructorDeclarationLineIndex(lines, parentName, {
        constructorClassName: frameworkClass.name,
        fallbackLineIndex0Based: lineNumber,
      })
    : Number.isFinite(lineNumber)
    ? lineNumber
    : null;
  if (lineIndex === null) return parseDriveUnitPortValue(frameworkClass.initialization?.find((item) => item.parameter === parameter)?.value);

  const rawValue = readConstructorKeywordValue(projectCode, lineIndex, portEntry.constructorArg);
  if (rawValue !== undefined) return parseDriveUnitPortValue(rawValue);

  const defaultInit = frameworkClass.initialization?.find((item) => item.parameter === parameter);
  return parseDriveUnitPortValue(defaultInit?.value);
}

/**
 * Derives drive-unit port occupancy from parsed hardware cards and the current editor model,
 * using the same `initializeParameter` path as `useDriveUnitPortRegistry`, so the Redux map
 * stays accurate even when Configure Hardware cards are not mounted.
 *
 * When no Monaco editor is available (e.g. dashboard), pass `projectCode` to resolve constructor
 * port literals from the loaded project's program text.
 */
export function buildDriveUnitPortOccupanciesFromHardwareCards(
  cards: ICardComponent[],
  editor: editor.IStandaloneCodeEditor | undefined,
  projectCode?: string
): DriveUnitPortOccupancy[] {
  const resolvedEditor = editor ?? cards.find((c) => c.props.editor)?.props.editor;
  const model = resolvedEditor?.getModel?.();
  const canUseEditor = !!model && model.getLineCount() >= 1;
  const canUseProjectCode = !canUseEditor && !!projectCode?.trim();

  if (!canUseEditor && !canUseProjectCode) {
    return [];
  }

  const rows: DriveUnitPortOccupancy[] = [];

  for (const card of cards) {
    const frameworkClass = card.props.mechanismClass;
    if (!frameworkClass) continue;

    const portEntries = getDriveUnitPortInitEntries(frameworkClass);
    if (!portEntries.length) continue;

    // Only cards rendered in Configure Hardware contribute; uiPanel must map to a known card component.
    if (!isHardwareCardType(frameworkClass.uiPanel)) continue;

    for (const { parameter } of portEntries) {
      let port: number | null = null;
      if (canUseEditor) {
        const props: DeviceCardProps = {
          ...card.props,
          editor: resolvedEditor ?? card.props.editor,
          parameterName: parameter,
        };
        port = parseDriveUnitPortValue(initializeParameter(props));
      } else if (canUseProjectCode) {
        port = resolvePortFromProjectCode(projectCode!, card.props.lineNumber, card.props.parentName, frameworkClass, parameter);
      }

      if (port === null || port < DRIVE_UNIT_PORT_MIN || port > DRIVE_UNIT_PORT_MAX) continue;
      rows.push({ driveUnitPort: port, electronic: `${card.props.id}:${parameter}` });
    }
  }

  const projectText = canUseEditor ? model!.getValue() : projectCode ?? '';
  return appendMechanismFollowerMotorPortRows(rows, cards, projectText);
}

function appendMechanismFollowerMotorPortRows(
  rows: DriveUnitPortOccupancy[],
  cards: ICardComponent[],
  projectCode: string
): DriveUnitPortOccupancy[] {
  if (!projectCode.trim()) return rows;

  const followers = parseMechanismFollowerMotorsFromProjectCode(projectCode);
  if (!followers.length) return rows;

  const occupiedPorts = new Set(rows.map((row) => row.driveUnitPort));
  const next = [...rows];

  for (const card of cards) {
    const parentName = card.props.parentName;
    if (!parentName) continue;

    const cardFollowers = followers.filter((follower) => follower.mechanismVariableName === parentName);
    for (const follower of cardFollowers) {
      if (follower.port < DRIVE_UNIT_PORT_MIN || follower.port > DRIVE_UNIT_PORT_MAX) continue;
      if (occupiedPorts.has(follower.port)) continue;

      next.push({
        driveUnitPort: follower.port,
        electronic: followerMotorPortElectronic(card.props.id, follower.port),
      });
      occupiedPorts.add(follower.port);
    }
  }

  return next;
}
